// PaymentMethodModal/RoutingCodeSection.tsx
import React, { useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import GlobalSelect from "@/components/ui/GlobalSelectItem";

import toast from "react-hot-toast";
import { BranchOption, FormSectionProps, IFSCResponse } from "./type";
import {
  getRoutingLabel,
  getRoutingMaxLength,
  getRoutingPlaceholder,
  validateIFSCCode,
  validateUSRoutingNumber,
} from "@/utils/PaymentMethodModalUtils";

export type TGlobalSelectOption = {
  id: string;
  value?: number | string;
  title: string;
};

interface RoutingCodeSectionProps extends FormSectionProps {
  selectedCountry: string;
  branches: BranchOption[] | [];
  loadingBranches: boolean;
  ifscError: string;
  setBranches: (branches: BranchOption[]) => void;
  setLoadingBranches: (loading: boolean) => void;
  setIfscError: (error: string) => void;
}

const RoutingCodeSection: React.FC<RoutingCodeSectionProps> = ({
  formData,
  selectedCountry,
  branches,
  loadingBranches,
  ifscError,
  onFormDataChange,
  setBranches,
  setLoadingBranches,
  setIfscError,
}) => {
  // Local state for branch selection
  const [selectedBranch, setSelectedBranch] =
    useState<TGlobalSelectOption | null>(() => {
      const currentBranchName = formData.details.branchName;
      if (currentBranchName && branches.length > 0) {
        const matchingBranch = branches.find(
          (branch) => branch.title === currentBranchName
        );
        if (matchingBranch) {
          return {
            id: matchingBranch.id,
            title: matchingBranch.title,
            value: matchingBranch.value,
          };
        }
      }
      return null;
    });

  // IFSC API integration
  const fetchBranchFromIFSC = async (ifscCode: string) => {
    setLoadingBranches(true);
    setIfscError("");

    const validation = validateIFSCCode(ifscCode);
    if (!validation.isValid) {
      setIfscError(validation.message || "Invalid IFSC code");
      setLoadingBranches(false);
      return;
    }

    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);

      if (!response.ok) {
        throw new Error("IFSC code not found");
      }

      const data: IFSCResponse = await response.json();

      const branchOption: BranchOption = {
        id: data.IFSC,
        title: data.BRANCH,
        value: data.IFSC,
        address: `${data.ADDRESS}, ${data.CITY}`,
      };

      setBranches([branchOption]);

      // Auto-fill form data
      onFormDataChange("bankName", data.BANK, true);
      onFormDataChange("branchName", data.BRANCH, true);
      onFormDataChange("bankAddress", `${data.ADDRESS}, ${data.CITY}`, true);
      onFormDataChange(
        "contactNumber",
        data.CONTACT || formData.details.contactNumber,
        true
      );
      onFormDataChange("country", "India", true);

      // Update selected branch state
      const newSelectedBranch: TGlobalSelectOption = {
        id: data.IFSC,
        title: data.BRANCH,
        value: data.IFSC,
      };
      setSelectedBranch(newSelectedBranch);
    } catch (error) {
      setIfscError("Invalid IFSC code or unable to fetch branch details");
      setBranches([]);
      setSelectedBranch(null);
      console.error("IFSC fetch error:", error);
    } finally {
      setLoadingBranches(false);
    }
  };

  const validateUSRouting = (routingNumber: string) => {
    const validation = validateUSRoutingNumber(routingNumber);
    if (!validation.isValid) {
      setIfscError(validation.message || "Invalid routing number");
      return;
    }

    setIfscError("");
    toast.success("Valid US routing number");
  };

  const handleRoutingCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    onFormDataChange("routingCode", value, true);

    // Handle different country routing codes
    if (selectedCountry === "IN" && value.length === 11) {
      fetchBranchFromIFSC(value);
    } else if (selectedCountry === "US" && value.length === 9) {
      validateUSRouting(value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
      setIfscError("");
    }
  };

  const handleBranchSelect = (option: TGlobalSelectOption | null) => {
    if (option) {
      setSelectedBranch(option);
      const selectedBranchData = branches.find(
        (branch) => branch.id === option.id
      );
      if (selectedBranchData) {
        onFormDataChange("branchName", selectedBranchData.title, true);
        onFormDataChange("bankAddress", selectedBranchData.address, true);
      }
    }
  };

  // Convert branches to TGlobalSelectOption format
  const branchOptions: TGlobalSelectOption[] = branches.map((branch) => ({
    id: branch.id,
    title: branch.title,
    value: branch.value,
  }));

  return (
    <div className="space-y-4">
      {/* Routing Code Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {getRoutingLabel(selectedCountry)}
        </label>
        <input
          type="text"
          name="routingCode"
          value={formData.details.routingCode}
          onChange={handleRoutingCodeChange}
          placeholder={getRoutingPlaceholder(selectedCountry)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#000200] ${
            ifscError ? "border-red-500" : "border-gray-300"
          }`}
          maxLength={getRoutingMaxLength(selectedCountry)}
          required
        />
        {loadingBranches && (
          <p className="text-sm text-blue-600 mt-1 flex items-center">
            <Loader2 size={16} className="mr-1 animate-spin" />
            Fetching branch details...
          </p>
        )}
        {ifscError && (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertCircle size={16} className="mr-1" />
            {ifscError}
          </p>
        )}
      </div>

      {/* Branch Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Branch Name
        </label>
        {branchOptions.length > 0 && selectedCountry === "IN" ? (
          <GlobalSelect
            position="relative"
            label=""
            options={branchOptions}
            selectedOption={selectedBranch}
            onSelect={handleBranchSelect}
            placeholder="Select Branch"
            showOtherInputField={false}
          />
        ) : (
          <input
            type="text"
            name="branchName"
            value={formData.details.branchName}
            onChange={(e) =>
              onFormDataChange("branchName", e.target.value, true)
            }
            placeholder={
              selectedCountry === "IN"
                ? "Enter IFSC code for auto-detection"
                : "Enter branch name"
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#000200]"
            required
          />
        )}
      </div>
    </div>
  );
};

export default RoutingCodeSection;
